<template>
  <div>
    <el-card shadow="hover">
      <div class="d-flex align-items-center justify-content-between mb-1">
        <div>
          <h4 class="mb-0">Bulk Notification (Preview)</h4>
          <small class="text-muted">Send a message to all users or selected users</small>
        </div>
        <div class="d-flex align-items-center">
          <el-button size="mini" type="info" plain @click="openPreview" :disabled="isSending || !isFormPotentiallyValid">
            Preview
          </el-button>
          <el-button size="mini" type="success" class="ml-1" :loading="isSending" :disabled="!isFormValid || isSending" @click="submitForm('ruleForm')">
            Send Now
          </el-button>
        </div>
      </div>

      <el-form ref="ruleForm" :model="ruleForm" :rules="rules" label-width="120px" class="demo-ruleForm">
        <!-- Message Details -->
        <div class="row">
          <div class="col-md-7">
            <el-form-item label="Title" prop="title">
              <el-input v-model="ruleForm.title" maxlength="150" show-word-limit placeholder="Enter concise title" />
            </el-form-item>

            <el-form-item label="Body" prop="body">
              <el-input v-model="ruleForm.body" type="textarea" :rows="6" maxlength="500" show-word-limit placeholder="Write your message..." />
              <div class="d-flex justify-content-between text-muted mt-1 small">
                <span>Use simple text. Max 500 chars.</span>
                <span>{{ ruleForm.body.length }}/500</span>
              </div>
            </el-form-item>

            <div class="d-flex align-items-center">
              <el-button type="primary" :loading="isSending" :disabled="!isFormValid || isSending" @click="submitForm('ruleForm')">Send</el-button>
              <el-button class="ml-1" @click="resetForm('ruleForm')">Reset</el-button>
              <el-divider direction="vertical" />
              <el-button type="text" @click="saveDraft" :disabled="!hasChanges">Save Draft</el-button>
              <el-button type="text" @click="loadDraft" :disabled="!hasDraft">Load Draft</el-button>
              <el-button type="text" @click="clearDraft" :disabled="!hasDraft">Clear Draft</el-button>
            </div>

            <div v-if="lastSavedAt" class="text-muted small mt-1">Last saved: {{ lastSavedAt }}</div>
          </div>

          <!-- Recipients -->
          <div class="col-md-5">
            <el-card class="mb-1" shadow="never" body-style="padding: 12px 16px;">
              <div class="d-flex align-items-center justify-content-between">
                <div class="font-weight-bold">Recipients</div>
                <el-tag :type="ruleForm.bulk ? 'success' : 'info'" size="mini" effect="plain">
                  {{ ruleForm.bulk ? 'All Users (Filtered)' : 'Specific Users' }}
                </el-tag>
              </div>
              <el-form-item class="mt-1 mb-0" label="All Users" prop="bulk">
                <el-switch v-model="ruleForm.bulk" active-color="#13ce66" inactive-color="#ff4949" />
              </el-form-item>

              <transition name="el-fade-in-linear">
                <div v-if="ruleForm.bulk">
                  <el-form-item label="Filter By" prop="filterby">
                    <el-select v-model="ruleForm.filterby" placeholder="Select Filter">
                      <el-option v-for="item in filters" :key="item.value" :label="item.label" :value="item.value" />
                    </el-select>
                  </el-form-item>
                  <div class="text-muted small">
                    Targeting: <strong>{{ currentFilterLabel }}</strong> users. Exact count depends on server-side filter.
                  </div>
                </div>
              </transition>

              <transition name="el-fade-in-linear">
                <div v-if="!ruleForm.bulk">
                  <el-form-item label="Select Users" prop="users">
                    <el-select
                      v-model="ruleForm.users"
                      multiple
                      filterable
                      remote
                      collapse-tags
                      reserve-keyword
                      placeholder="Type Username"
                      :remote-method="remoteMethod"
                      :loading="loading"
                    >
                      <el-option
                        v-for="item in userLists"
                        :key="item.user_id"
                        :label="item.user_name"
                        :value="item.user_id"
                      />
                    </el-select>
                  </el-form-item>
                  <div class="small text-muted">Selected: {{ ruleForm.users.length }} user(s)</div>

                  <div class="mt-1">
                    <el-tag
                      v-for="chip in selectedUserChips"
                      :key="chip.user_id"
                      size="mini"
                      closable
                      @close="removeUser(chip.user_id)"
                      class="mr-1 mb-1"
                    >
                      {{ chip.user_name }}
                    </el-tag>
                  </div>
                </div>
              </transition>
            </el-card>

            <el-alert
              v-if="!isFormPotentiallyValid"
              title="Please complete required fields before sending"
              type="warning"
              :closable="false"
              show-icon
            />
          </div>
        </div>
      </el-form>
    </el-card>

    <!-- Preview Dialog -->
    <el-dialog title="Preview Notification" :visible.sync="previewVisible" width="600px">
      <div>
        <div class="mb-1">
          <div class="text-muted small">Title</div>
          <div class="font-weight-bold">{{ ruleForm.title || '—' }}</div>
        </div>
        <div class="mb-1">
          <div class="text-muted small">Body</div>
          <div style="white-space: pre-wrap">{{ ruleForm.body || '—' }}</div>
        </div>
        <div>
          <div class="text-muted small">Recipients</div>
          <div>
            <el-tag :type="ruleForm.bulk ? 'success' : 'info'" size="mini" effect="plain" class="mr-1">
              {{ ruleForm.bulk ? 'All Users' : 'Specific Users' }}
            </el-tag>
            <span v-if="ruleForm.bulk">Filter: <strong>{{ currentFilterLabel }}</strong></span>
            <span v-else>Selected: <strong>{{ ruleForm.users.length }}</strong> user(s)</span>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="previewVisible = false">Close</el-button>
        <el-button type="primary" :loading="isSending" :disabled="!isFormValid || isSending" @click="submitForm('ruleForm')">Send</el-button>
      </span>
    </el-dialog>
  </div>
  
</template>

<script>
import { SEARCH_USERS, SEND_NOTIFICATION } from '@core/services/api'

export default {
  name: 'BulkNotificationV2',
  data() {
    return {
      ruleForm: {
        title: '',
        body: '',
        bulk: false,
        filterby: 'all',
        users: [],
      },
      filters: [
        { value: 'all', label: 'All' },
        { value: 'paid', label: 'Paid' },
        { value: 'unpaid', label: 'Unpaid' },
      ],
      rules: {
        title: [
          { required: true, message: 'Please input Title', trigger: 'blur' },
          { max: 150, message: 'Length should be 150 char', trigger: 'blur' },
        ],
        body: [
          { required: true, message: 'Please input Body', trigger: 'blur' },
          { max: 500, message: 'Length should be 500 char', trigger: 'blur' },
        ],
        users: [
          { validator: (rule, val, cb) => {
              // If not bulk, require at least one user
              if (!this.ruleForm.bulk && (!val || !val.length)) return cb(new Error('Select at least one user'))
              cb()
            }, trigger: 'change' },
        ],
      },
      userLists: [],
      loading: false,
      isSending: false,
      searchDelay: null,
      autosaveDelay: null,
      previewVisible: false,
      isp_id: JSON.parse(localStorage.getItem('userData')),
      // draft
      lastSavedAt: '',
      initialSnapshot: null,
    }
  },
  computed: {
    hasDraft() {
      return !!localStorage.getItem('bulkNotificationDraft')
    },
    hasChanges() {
      return JSON.stringify(this.ruleForm) !== JSON.stringify(this.initialSnapshot)
    },
    currentFilterLabel() {
      const found = this.filters.find(f => f.value === this.ruleForm.filterby)
      return found ? found.label : this.ruleForm.filterby
    },
    isFormPotentiallyValid() {
      if (!this.ruleForm.title || !this.ruleForm.body) return false
      if (!this.ruleForm.bulk && !this.ruleForm.users.length) return false
      return true
    },
    isFormValid() {
      // We’ll trust validate() for final check, but keep a quick computed for button state
      return this.isFormPotentiallyValid
    },
    selectedUserChips() {
      // Build chip list from available userLists + selected IDs
      const map = new Map(this.userLists.map(u => [u.user_id, u]))
      return (this.ruleForm.users || []).map(id => map.get(id)).filter(Boolean)
    },
  },
  created() {
    this.initialSnapshot = JSON.parse(JSON.stringify(this.ruleForm))
    const saved = localStorage.getItem('bulkNotificationDraft')
    if (saved) {
      // don’t auto-load, but let user choose via Load Draft
      // noop
    }
  },
  watch: {
    ruleForm: {
      handler() {
        // autosave debounce
        clearTimeout(this.autosaveDelay)
        this.autosaveDelay = setTimeout(() => {
          localStorage.setItem('bulkNotificationDraft', JSON.stringify(this.ruleForm))
          this.lastSavedAt = new Date().toLocaleString()
        }, 500)
      },
      deep: true,
    },
  },
  methods: {
    openPreview() {
      if (!this.isFormPotentiallyValid) {
        this.$message.warning('Please complete required fields to preview')
        return
      }
      this.previewVisible = true
    },
    submitForm(formName) {
      this.$refs[formName].validate(valid => {
        if (!valid) return
        const payload = { ...this.ruleForm, isp_id: this.isp_id && this.isp_id.id ? this.isp_id.id : undefined }
        this.isSending = true
        this.$http
          .post(process.env.VUE_APP_BASEURL + SEND_NOTIFICATION, payload)
          .then(res => {
            this.isSending = false
            this.previewVisible = false
            this.$notify({ type: 'success', message: res && res.data && res.data.messages ? res.data.messages : 'Notification sent' })
            this.resetForm(formName)
          })
          .catch(err => {
            this.isSending = false
            const msg = (err && err.response && err.response.data && err.response.data.message) || err.message || 'Failed to send'
            this.$notify({ type: 'error', message: msg })
          })
      })
    },
    resetForm(formName) {
      this.$refs[formName] && this.$refs[formName].resetFields()
      this.ruleForm.filterby = 'all'
      this.userLists = []
      this.previewVisible = false
      this.initialSnapshot = JSON.parse(JSON.stringify(this.ruleForm))
    },
    remoteMethod(query) {
      if (!query || query.length < 3) return
      if (this.searchDelay) clearTimeout(this.searchDelay)
      this.searchDelay = setTimeout(() => {
        this.loading = true
        this.$http
          .get(process.env.VUE_APP_BASEURL + SEARCH_USERS, {
            params: {
              search_query: query,
              isp_id: this.isp_id && this.isp_id.id ? this.isp_id.id : undefined,
            },
          })
          .then(res => {
            const users = res && res.data && res.data.data && res.data.data.users ? res.data.data.users : []
            this.userLists = Array.isArray(users) ? users : []
          })
          .catch(() => {})
          .finally(() => {
            this.loading = false
          })
      }, 350)
    },
    removeUser(id) {
      this.ruleForm.users = (this.ruleForm.users || []).filter(u => u !== id)
    },
    saveDraft() {
      localStorage.setItem('bulkNotificationDraft', JSON.stringify(this.ruleForm))
      this.lastSavedAt = new Date().toLocaleString()
      this.$message.success('Draft saved')
    },
    loadDraft() {
      const saved = localStorage.getItem('bulkNotificationDraft')
      if (!saved) return
      try {
        const parsed = JSON.parse(saved)
        this.ruleForm = Object.assign({}, this.ruleForm, parsed)
        this.$message.success('Draft loaded')
      } catch (e) {
        this.$message.error('Failed to load draft')
      }
    },
    clearDraft() {
      localStorage.removeItem('bulkNotificationDraft')
      this.lastSavedAt = ''
      this.$message.success('Draft cleared')
    },
  },
}
</script>

<style scoped>
.row {
  margin-right: -8px;
  margin-left: -8px;
}
.col-md-7, .col-md-5 {
  padding-right: 8px;
  padding-left: 8px;
}
</style>
